import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import type { Branch } from '@/lib/shared-types';
import { localizedName } from '@/lib/localized';
import type { CatalogItem, Category } from '@/Pages/Catalog/lib';
import {
  ContactChannel,
  type CustomerTag,
  type SegmentRule,
  type SegmentVocabulary,
  type SegmentVocabularyField,
} from '../lib';
import { blankRule, monthLabel } from './segmentEditorLib';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * The curated predicate editor.
 *
 * There is no free-text query surface anywhere in here, on purpose (ADR-0010
 * §3): every field, operator and value comes off `GET /segments/vocabulary` or
 * off one of the tenant's own reference lists (branches, tags, catalog items,
 * categories), never off a text box that reaches the query. A `LOYALTY_TIER`
 * value is the one deliberate exception — `FIELD_SPECS.LOYALTY_TIER` carries no
 * `enumValues` because `LoyaltyAccount.tier` has no table behind it yet
 * (`segment-definition.ts`'s own comment says so) — so that one field alone
 * accepts a typed tag rather than a picked one.
 */
export default function SegmentRuleEditor({
  vocabulary,
  match,
  onMatchChange,
  rules,
  onRulesChange,
}: {
  vocabulary: SegmentVocabulary;
  match: string;
  onMatchChange: (match: string) => void;
  rules: SegmentRule[];
  onRulesChange: (rules: SegmentRule[]) => void;
}) {
  const { t } = useTranslation();
  const specByField = useMemo(
    () => new Map(vocabulary.fields.map((f) => [f.field, f])),
    [vocabulary.fields],
  );

  const addRule = () => {
    const first = vocabulary.fields[0];
    if (!first) return;
    onRulesChange([...rules, blankRule(first)]);
  };
  const updateRule = (index: number, next: SegmentRule) =>
    onRulesChange(rules.map((r, i) => (i === index ? next : r)));
  const removeRule = (index: number) => onRulesChange(rules.filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      <Field
        label={t('crm.segments.match')}
        hint={t('crm.segments.matchHint')}
      >
        <Select value={match} onChange={(e) => onMatchChange(e.target.value)}>
          {vocabulary.match.map((m) => (
            <option key={m} value={m}>
              {t(`crm.segments.matchMode.${m}`)}
            </option>
          ))}
        </Select>
      </Field>

      <div className="space-y-2">
        {rules.length === 0 && (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-ink-muted">
            {t('crm.segments.noRulesYet')}
          </p>
        )}
        {rules.map((rule, index) => {
          const spec = specByField.get(rule.field);
          if (!spec) return null;
          return (
            <div key={index} className="flex flex-wrap items-start gap-2 rounded-lg border p-3">
              <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <Field label={t('crm.segments.field')}>
                  <Select
                    value={rule.field}
                    onChange={(e) => {
                      const nextSpec = specByField.get(e.target.value);
                      if (nextSpec) updateRule(index, blankRule(nextSpec));
                    }}
                  >
                    {vocabulary.fields.map((f) => (
                      <option key={f.field} value={f.field}>
                        {t(`crm.segments.fieldName.${f.field}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('crm.segments.operator')}>
                  <Select
                    value={rule.operator}
                    onChange={(e) =>
                      updateRule(index, { ...rule, operator: e.target.value as SegmentRule['operator'] })
                    }
                  >
                    {spec.operators.map((op) => (
                      <option key={op} value={op}>
                        {t(`crm.segments.operatorValue.${op}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <div className="sm:col-span-2">
                  <RuleValueEditor
                    spec={spec}
                    rule={rule}
                    onChange={(next) => updateRule(index, next)}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('crm.segments.removeRule')}
                onClick={() => removeRule(index)}
              >
                <Icon as={Trash2} />
              </Button>
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRule}>
        <Icon as={Plus} size="sm" className="me-1.5" />
        {t('crm.segments.addRule')}
      </Button>
      {rules.length >= vocabulary.maxRules && (
        <p className="text-xs text-ink-muted">
          {t('crm.segments.maxRulesReached', { count: vocabulary.maxRules })}
        </p>
      )}
    </div>
  );
}

function RuleValueEditor({
  spec,
  rule,
  onChange,
}: {
  spec: SegmentVocabularyField;
  rule: SegmentRule;
  onChange: (rule: SegmentRule) => void;
}) {
  const { t, i18n } = useTranslation();

  if (spec.valueKind === 'presence') {
    return (
      <p className="pt-2 text-sm text-ink-muted">{t('crm.segments.noValueNeeded')}</p>
    );
  }

  if (spec.valueKind === 'number') {
    return (
      <Field label={t('crm.segments.value')}>
        <Input
          type="number"
          value={rule.value ?? ''}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        />
      </Field>
    );
  }

  if (spec.valueKind === 'money') {
    return (
      <Field label={t('crm.segments.value')} hint={t('crm.segments.moneyHint')}>
        <Input
          inputMode="decimal"
          placeholder="2000.00"
          value={typeof rule.value === 'string' ? rule.value : ''}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        />
      </Field>
    );
  }

  if (spec.valueKind === 'consent') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Field label={t('crm.segments.consentChannel')}>
          <Select
            value={rule.channel ?? ContactChannel.SMS}
            onChange={(e) => onChange({ ...rule, channel: e.target.value as SegmentRule['channel'] })}
          >
            {Object.values(ContactChannel).map((c) => (
              <option key={c} value={c}>
                {t(`crm.consent.channel.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('crm.segments.consentStatus')}>
          <Select
            value={typeof rule.value === 'string' ? rule.value : ''}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
          >
            {(spec.allowedValues ?? []).map((v) => (
              <option key={v} value={v}>
                {t(`crm.consent.status.${v}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    );
  }

  if (spec.valueKind === 'monthList') {
    const selected = new Set((rule.values ?? []).map(Number));
    return (
      <Field label={t('crm.segments.months')}>
        <div className="flex flex-wrap gap-1.5">
          {MONTHS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() =>
                onChange({
                  ...rule,
                  values: selected.has(m)
                    ? [...selected].filter((v) => v !== m)
                    : [...selected, m],
                })
              }
              className={
                'rounded-full border px-2.5 py-1 text-xs ' +
                (selected.has(m)
                  ? 'border-accent bg-accent-soft text-accent-soft-fg'
                  : 'border-line-strong hover:bg-hover')
              }
            >
              {monthLabel(m, i18n.language)}
            </button>
          ))}
        </div>
      </Field>
    );
  }

  if (spec.valueKind === 'enumList' && spec.allowedValues) {
    const selected = new Set(rule.values ?? []);
    // ORDER_CHANNEL is the one enumList with a fixed allowed set and a value
    // this app already has a label for — `order.fulfillment.<value>`, the same
    // key `CustomerDetail` uses for an order row's own fulfillment type.
    const labelFor = (value: string) =>
      spec.field === 'ORDER_CHANNEL' ? t(`order.fulfillment.${value}`, value) : value;
    return (
      <Field label={t('crm.segments.value')}>
        <div className="flex flex-wrap gap-1.5">
          {spec.allowedValues.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() =>
                onChange({
                  ...rule,
                  values: selected.has(v)
                    ? (rule.values ?? []).filter((x) => x !== v)
                    : [...(rule.values ?? []), v],
                })
              }
              className={
                'rounded-full border px-2.5 py-1 text-xs ' +
                (selected.has(v)
                  ? 'border-accent bg-accent-soft text-accent-soft-fg'
                  : 'border-line-strong hover:bg-hover')
              }
            >
              {labelFor(v)}
            </button>
          ))}
        </div>
      </Field>
    );
  }

  // enumList with no allowedValues: only LOYALTY_TIER lands here, and it is
  // the one deliberate exception to "no free text" — see the module doc above.
  if (spec.valueKind === 'enumList') {
    return (
      <FreeTextChips
        label={t('crm.segments.loyaltyTiers')}
        hint={t('crm.segments.loyaltyTiersHint')}
        values={(rule.values ?? []).map(String)}
        onChange={(values) => onChange({ ...rule, values })}
      />
    );
  }

  if (spec.valueKind === 'idList') {
    return (
      <ReferencePicker
        reference={spec.reference}
        values={(rule.values ?? []).map(String)}
        onChange={(values) => onChange({ ...rule, values })}
      />
    );
  }

  return null;
}

function FreeTextChips({
  label,
  hint,
  values,
  onChange,
}: {
  label: string;
  hint?: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  return (
    <Field label={label} hint={hint}>
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              }
            }}
          />
          <Button type="button" variant="outline" size="sm" onClick={commit}>
            <Icon as={Plus} size="sm" />
          </Button>
        </div>
        {values.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {values.map((v) => (
              <Chip key={v} onRemove={() => onChange(values.filter((x) => x !== v))}>
                {v}
              </Chip>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

/** Branch / tag: a checklist. Catalog item / category: search-and-pick, mirroring `MenuGovernance`'s item picker. */
function ReferencePicker({
  reference,
  values,
  onChange,
}: {
  reference: SegmentVocabularyField['reference'];
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const toggle = (id: string) =>
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);

  // Every hook below is called on every render regardless of `reference` —
  // switching a rule's field switches which branch of the JSX below renders,
  // not which hooks run, which is what the Rules of Hooks require whenever a
  // component's prop can change without it being unmounted (this one is not
  // re-keyed when a rule's field changes, only when the rule is removed). The
  // `url` argument being `null` is what actually skips the unwanted fetches;
  // `useApi` itself is always called.
  const [search, setSearch] = useState('');
  const branches = useApi<unknown>(reference === 'branch' ? '/branches' : null, { limit: 200 });
  const tags = useApi<unknown>(reference === 'tag' ? '/customer-tags' : null, { limit: 200 });
  const categories = useApi<unknown>(reference === 'category' ? '/categories' : null, { limit: 200 });
  const items = useApi<unknown>(reference === 'catalogItem' ? '/catalog' : null, {
    limit: 50,
    search: search || undefined,
  });

  if (reference === 'branch') {
    const rows = rowsOf<Branch>(branches.data).rows;
    return (
      <Field label={t('crm.segments.branches')}>
        <ChecklistBox
          loading={branches.loading}
          options={rows.map((b) => ({ id: b.id, label: b.name }))}
          selected={values}
          onToggle={toggle}
        />
      </Field>
    );
  }

  if (reference === 'tag') {
    const rows = rowsOf<CustomerTag>(tags.data).rows;
    return (
      <Field label={t('crm.segments.tags')}>
        <ChecklistBox
          loading={tags.loading}
          options={rows.map((tag) => ({ id: tag.id, label: tag.name }))}
          selected={values}
          onToggle={toggle}
        />
      </Field>
    );
  }

  if (reference === 'category') {
    const rows = rowsOf<Category>(categories.data).rows;
    return (
      <Field label={t('crm.segments.categories')}>
        <ChecklistBox
          loading={categories.loading}
          options={rows.map((c) => ({ id: c.id, label: localizedName(c, i18n.language) }))}
          selected={values}
          onToggle={toggle}
        />
      </Field>
    );
  }

  // catalogItem — the one reference list too large for a fixed checklist.
  const rows = rowsOf<CatalogItem>(items.data).rows;
  return (
    <Field label={t('crm.segments.products')}>
      <div className="space-y-1.5">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('crm.segments.searchProducts')}
        />
        <ChecklistBox
          loading={items.loading}
          options={rows.map((i) => ({ id: i.id, label: localizedName(i, i18n.language) }))}
          selected={values}
          onToggle={toggle}
        />
      </div>
    </Field>
  );
}

function ChecklistBox({
  loading,
  options,
  selected,
  onToggle,
}: {
  loading: boolean;
  options: Array<{ id: string; label: string }>;
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border p-2">
      {loading ? (
        <p className="p-2 text-sm text-ink-muted">{t('common.loading')}</p>
      ) : options.length === 0 ? (
        <p className="p-2 text-sm text-ink-muted">{t('crm.segments.noOptions')}</p>
      ) : (
        options.map((opt) => (
          <label key={opt.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-hover">
            <input
              type="checkbox"
              checked={selected.includes(opt.id)}
              onChange={() => onToggle(opt.id)}
              className="h-4 w-4 accent-[var(--accent-solid)]"
            />
            <span className="truncate">{opt.label}</span>
          </label>
        ))
      )}
    </div>
  );
}

function Chip({ children, onRemove }: { children: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-subtle px-2.5 py-1 text-xs">
      {children}
      <button type="button" onClick={onRemove} aria-label="remove" className="text-ink-muted hover:text-ink">
        ×
      </button>
    </span>
  );
}
